// Core game data model. Everything here is plain JSON-serializable data —
// the whole world state persists to disk as-is. Shared. FROZEN CONTRACT.

import { Terrain } from './terrain'
import type { RealmProgress } from './realm'
import type { GovernorState } from './governor'
import type { CampState } from './camp'
import type { NeutralAIState, NeutralPublicStatus } from './neutral'

// ---- Resources --------------------------------------------------------------

export type Resource = 'food' | 'wood' | 'stone' | 'iron' | 'gold' | 'dirt' | 'sand'
export const RESOURCES: readonly Resource[] = ['food', 'wood', 'stone', 'iron', 'gold', 'dirt', 'sand']
export type Stockpile = Record<Resource, number>

/** One storage building's share of a resource, for the "stored in" hover breakdown. */
export interface StoreLine {
  label: string
  qty: number
  cap: number
  /** Building id this line belongs to, so panels can match the clicked hub.
   *  (Additive optional wire field.) */
  id?: number
}

export function emptyStockpile(): Stockpile {
  return { food: 0, wood: 0, stone: 0, iron: 0, gold: 0, dirt: 0, sand: 0 }
}

// ---- Map --------------------------------------------------------------------

/**
 * One 16x16 chunk of the infinite map.
 * tiles: flat row-major Terrain codes (see shared/hex.ts localIdx/tileFromIdx).
 * roads: local tile indices that have a road built on them.
 * owners: local tile index -> faction id (territory claims).
 */
export interface Chunk {
  cx: number
  cy: number
  tiles: number[]
  roads: number[]
  owners: Record<string, string>
  /** Items dropped on the ground (keyed by local hex index) — e.g. a slain unit's
   *  tool or weapon, waiting to be scooped up by a passing citizen. */
  drops?: Record<string, ItemType>
  /** Resource piles spilled on the ground (keyed by local hex index) — e.g. a razed
   *  town hall's stores, waiting for the owner's haulers to cart them to a depot. */
  spills?: Record<string, SpillPile>
  /** Buried surfaces under terraform fills (keyed by local hex index): each fill
   *  pushes the terrain it covered; a dig pops it back. Server-only bookkeeping —
   *  never sent on the wire. Additive optional. */
  strata?: Record<string, number[]>
  /** Optional per-tile height tier (integer, sea level = 0), parallel to `tiles`.
   *  Present only when the elevation feature is enabled at worldgen; absent on
   *  flat/legacy chunks. Additive optional field — see shared/elevation.ts. */
  elev?: number[]
  /** Optional per-tile standing-tree flags (1 = a choppable tree grows on this
   *  otherwise-ordinary grass hex), parallel to `tiles`. Woodland is no longer a
   *  distinct biome: worldgen sows trees onto grass, chopping clears the flag, and
   *  the renderers draw a tree wherever this is set. Absent on treeless chunks. */
  trees?: number[]
  /** Earliest wolf replenishment tick; server-only, persisted across reloads. */
  wolfRespawnAt?: number
}

/** A heap of goods sitting on the floor, tagged with the faction whose haulers own it. */
export interface SpillPile {
  owner: string
  goods: Partial<Stockpile>
}

// ---- Factions (players and NPCs) ---------------------------------------------

export type FactionKind = 'player' | 'npc'

/** What every client is allowed to know about a faction. */
export interface FactionPublic {
  id: string
  kind: FactionKind
  /** An autonomous neutral settlement using the same economy as player villages. */
  controller?: 'neutral'
  /** Public intentions of a computer-controlled village; no scouting intel. */
  npc?: NeutralPublicStatus
  name: string
  /** CSS color for territory/units on everyone's screen. */
  color: string
  /** Faction ids this faction is allied with (mutual). */
  allies: string[]
  online?: boolean
  /** NPC difficulty tier 0..3 (0 peaceful hamlet … 3 warlord). Undefined for players. */
  tier?: number
  home?: { q: number; r: number }
  /** True once the faction has lost its town hall and all buildings. */
  ruined?: boolean
  /** True when the player left this realm behind (restarted). Its buildings decay fast and it's uncontrolled. */
  abandoned?: boolean
}

/** One distinct source IP a player has authenticated from (admin audit only). */
export interface IpSeen {
  ip: string
  /** ms epoch first and most-recent login from this address. */
  first: number
  last: number
  /** Number of logins seen from this address. */
  count: number
}

/** Full server-side faction record. NEVER send token/stockpile to other clients. */
export interface Faction extends FactionPublic {
  /** Private military plans, observations and diplomatic memory. */
  neutralAI?: NeutralAIState
  /** Recent attackers and their defense-expiry ticks; server-only and bounded. */
  neutralAggressors?: Record<string, number>
  /** Private, additive progression data; legacy saves initialize on first use. */
  realm?: RealmProgress
  governor?: GovernorState
  /** Private settlement project and discovered places. */
  camp?: CampState
  /** New arrivals need food in storage and housing, or can be paused by the player. */
  growth?: 'steady' | 'paused'
  /** New villages begin with two founders and campsite housing for five. */
  startingPair?: boolean
  /** Secret login token (players only). */
  token?: string
  stockpile: Stockpile
  /** Max total stored per resource. */
  capacity: number
  /** The armory: real crafted items you own that aren't in someone's hands. */
  items: Partial<Record<ItemType, number>>
  /** Desired spare gear on the armory rack; all minimums together fit its shared capacity. */
  armoryMinimums?: Partial<Record<ItemType, number>>
  /** Alliance proposals received (faction ids). */
  proposals: string[]
  lastSeen: number
  /** Every source IP this player has logged in from (admin-only; persisted, bounded). */
  ips?: IpSeen[]
  /** Server tick bookkeeping for NPC AI / growth timers. */
  ai?: Record<string, number>
}

export const WILD = 'wild' // owner id for animals / raider camps

// ---- Buildings ----------------------------------------------------------------

export type BuildingType =
  | 'townhall'
  | 'house'
  | 'farm'
  | 'chop' // felling order: an axe-serf chops a forest tile down for wood, then it vanishes
  | 'plant' // forestry order: a serf plants a sapling, which matures after three game days
  | 'quarry'
  | 'mine'
  | 'storehouse'
  | 'barracks'
  | 'workbench'
  | 'dummy'
  | 'palisade'
  | 'pier'
  | 'tower'
  | 'ramp' // oriented: bridges one cliff edge so folk can climb it (elevation feature)
  | 'dig' // terraform order: a shovel-serf lowers the hex one height step, then it vanishes
  | 'fill' // terraform order: 10 dirt + shovel-work raises the hex one height step
  | 'fillsand' // as 'fill' but packed with 10 sand — the hex becomes sand
  | 'road'
  | 'camp' // raider camp (WILD-owned)

/** A queued build order at a training/forge building. Exactly one of `unit` (spawns
 *  a unit — NPC/legacy) or `item` (forges gear into the faction armory) is set. */
export interface TrainOrder {
  /** Existing surplus equipment awaiting rack space after a capacity migration; no crafting reward. */
  armoryReturn?: boolean
  automatic?: boolean
  unit?: UnitType
  /** Forge this item into the town-hall armory on completion (Smithy). */
  item?: ItemType
  ticksLeft: number
}

export interface Building {
  /** Town hall architecture, independent of site-prep `level`. Absent = campsite. */
  hallPhase?: import('./townhall').TownHallPhase
  /** Paid town hall upgrade. Phase is the target; the current hall keeps working. */
  hallUpgrade?: { phase: import('./townhall').TownHallPhase; started: number; ends: number }
  manualStaffing?: boolean
  id: number
  type: BuildingType
  q: number
  r: number
  owner: string
  /** Player-chosen label; when unset the display falls back to BUILDINGS[type].name. */
  name?: string
  hp: number
  /** True once construction finished. */
  done: boolean
  /** Sapling maturity deadline in simulation ticks; set when a plant order is finished. */
  treeGrowsAt?: number
  /** Construction progress in builder-ticks accumulated (vs def.buildTime). */
  progress: number
  /** Site-prep felling progress against a standing tree still on the footprint: the
   *  crew clears the trees (dropping their wood) before levelling or building.
   *  Resets to 0 as each tree comes down. Server-only/transient; additive. */
  fell?: number
  /** Site-prep levelling progress against the current off-target footprint hex (one
   *  tier step; resets per step). The crew regrades the plot to levelTarget() after
   *  the trees are down and before materials arrive. Server-only; additive. */
  level?: number
  /** Site-prep earth kept on the plot: cuts bank 10 per tier dug, fills consume 10
   *  per tier raised (spoil first, hauled dirt after). Leftovers spill as dirt when
   *  prep completes or the site is cancelled. Server-only; additive. */
  spoil?: number
  /** Locally buffered produced goods awaiting carrier pickup. */
  buffer: Partial<Stockpile>
  /** Maintenance store: materials stocked for the daily upkeep bill (haulers top
   *  it up; decay consumes it). Owner-only on the wire. Additive/optional. */
  mstore?: Partial<Stockpile>
  /** Unit ids currently working this building (staffing production / hauling home). */
  workers: number[]
  queue: TrainOrder[]
  rally?: { q: number; r: number }
  /** Generic cooldown counter (tower shots, camp spawns). */
  cooldown: number
  /** Owner paused this building: production/training stop and workers disperse,
   *  but haulers still drain the buffer. (Additive optional wire field.) */
  paused?: boolean
  /** Construction-supply priority for sites, −3…+3 (absent = 0, normal).
   *  Carriers serve higher-priority sites first. Cleared when finished. */
  prio?: number
  /** Hubs only: the goods physically kept in THIS building. The faction
   *  stockpile is a derived cache of all hub stores. (Additive; foe-stripped.) */
  store?: Partial<Stockpile>
  /** Hubs only: per-resource acceptance. Absent/true = accepted; explicit
   *  false = this hub refuses deposits of that resource. (Additive; foe-stripped.) */
  accept?: Partial<Record<Resource, boolean>>
  /** Hubs only: per-resource stock limits. max = never store more than this
   *  here; min = reserve kept here that construction supply won't draw below.
   *  (Additive; foe-stripped.) */
  lim?: Partial<Record<Resource, { min?: number; max?: number }>>
  /** Hubs only: storage priority −3…+3. Deposits fill high-priority hubs
   *  first; spending drains low-priority hubs first. (Additive; foe-stripped.) */
  sprio?: number
  /** Dedicated crew (unit ids). Empty/absent = anyone may work here; non-empty
   *  = ONLY these serfs take this building's worker slots. (Additive; foe-stripped.) */
  crew?: number[]
  /** Oriented buildings (the fishing pier): DIRS index from the anchor hex to
   *  the building's second cell. (Additive.) */
  dir?: number
}

// ---- Units ---------------------------------------------------------------------

export type UnitType =
  | 'citizen' // the one player unit: a person with skills, attributes and gear
  | 'worker' // legacy/NPC labour (still used by some NPC paths)
  | 'carrier'
  | 'spearman' // NPC garrison/raid soldiers
  | 'archer'
  | 'raider'
  | 'wolf'
  | 'bear'
  | 'deer'

// ---- People: skills, attributes, age (player 'citizen' units) -------------------

/** Learned abilities that rise with use/training and rust when idle (0..SKILL_MAX). */
export type SkillKey =
  | 'build'
  | 'farm'
  | 'log'
  | 'mine'
  | 'haul'
  | 'fish'
  | 'craft'
  | 'unarmed'
  | 'spear'
  | 'sword'
  | 'bow'

/** Slow-changing body/mind attributes. */
export type AttrKey = 'str' | 'spd' | 'int' | 'cha' | 'vit'

/** Real, craftable items a person can carry. Tools enable work; weapons, combat. */
export type ItemType =
  | 'hammer'
  | 'smithhammer'
  | 'rod'
  | 'shovel'
  | 'hoe'
  | 'axe'
  | 'pick'
  | 'spear'
  | 'sword'
  | 'bow'

export type ItemKind = 'tool' | 'weapon'

export interface Person {
  /** Display name, e.g. "Bram of the Vale". */
  name: string
  /** Age in ticks (immortal for now — tracked and shown only). */
  age: number
  /** Attribute levels, 0..ATTR_MAX. */
  attr: Record<AttrKey, number>
  /** Current skill levels (float), 0..SKILL_MAX. */
  skill: Record<SkillKey, number>
  /** Highest level ever reached per skill — the rust floor is peak/3. */
  peak: Record<SkillKey, number>
  /** Tick each skill was last exercised, for the rust grace period. */
  used: Record<SkillKey, number>
  /** The one real item they're holding, if any. No item ⇒ bare hands / fists. */
  held?: ItemType
  /** Hunger meter, 0..FOOD_METER_MAX. Drains over time; eating tops it up. */
  food: number
  /** Food units carried in their satchel (inventory), eaten when peckish. Each has FOOD_WEIGHT. */
  foodItems: number
  /** Tick the meter hit empty (undefined while they still have food) — for the starvation clock. */
  emptySince?: number
  /** Tick they FIRST ran empty (unlike emptySince this never resets while
   *  starving) — powers the "starving for N hours" display. */
  starvedAt?: number
}

export type UnitState =
  | 'idle'
  | 'moving' // player-ordered move
  | 'toJob' // walking to assigned building
  | 'working' // staffing a production building
  | 'building' // constructing a site
  | 'hauling' // carrier trip
  | 'waitingStorage' // holding cargo until a reachable store has space
  | 'errand' // walking to the town-hall armory to swap the held item
  | 'feeding' // walking to the granary to eat & restock food
  | 'fighting'
  | 'fleeing'
  | 'roaming' // animals / npc patrol

export interface Unit {
  /** Player-directed, continuous exploration using ordinary movement and vision. */
  exploring?: boolean
  /** Bounded route memory and recall state; persisted, never sent to clients. */
  exploration?: { visited: { q: number; r: number }[]; next: number; returning?: boolean }
  /** Keep direct player orders out of automatic staffing for a short period. */
  manualUntil?: number
  id: number
  type: UnitType
  owner: string
  /** Current logical hex. */
  q: number
  r: number
  /** Previous hex, for client interpolation. */
  fq: number
  fr: number
  /** Progress 0..1 from (fq,fr) toward (q,r). */
  mp: number
  hp: number
  state: UnitState
  /** Remaining path hexes (next hop first). Server-only detail, stripped on the wire. */
  path?: { q: number; r: number }[]
  /** Attack-move / aggro target entity id ('u' unit or 'b' building). */
  target?: { kind: 'u' | 'b'; id: number }
  /** Assigned building id (job site, construction site, or carrier home). */
  job?: number
  carrying?: Partial<Stockpile>
  /** Original owner of physically carried raid spoils. Public with the visible
   *  cargo; recovered goods return to this faction if the carrier is killed. */
  raidLoot?: string
  /** Ticks until this unit re-evaluates AI (cheap stagger). */
  think: number
  /** Attack cooldown ticks remaining. */
  atkCd: number
  /** Present on 'citizen' units: their skills, attributes, age and held item. */
  person?: Person
  /** Pending armory swap: walk to town-hall `hall` and change held item to `to`.
   *  On arrival, `swap` counts down the deposit/collect moment before the item changes. */
  errand?: { to: ItemType | null; hall: number; swap?: number }
  /** Citizen currently on hub haul duty (auto-managed logistics). */
  hauler?: boolean
  /** Carrier on a construction-supply run: the site id it is loading for /
   *  delivering to. (Additive optional wire field; stripped for non-owners.) */
  supply?: number
  /** Idle serf out scavenging the wilds for wood/stone. (Additive optional
   *  wire field; stripped for non-owners.) */
  scav?: boolean
  /** Player opt-in: this serf may scavenge the wilds when idle. Absent = off —
   *  serfs only comb the wilds when told to. (Additive optional wire field;
   *  stripped for non-owners.) */
  scavOn?: boolean
  /** Combat stance override: true = fight back when attacked (even bare-handed),
   *  false = always flee (even armed). Unset = the default: fight back.
   *  (Additive optional wire field; stripped for non-owners.) */
  defend?: boolean
  /** Armed serf's patrol post: they amble within guard range of this hex when
   *  idle. Set where they first stand watch; cleared by player orders.
   *  (Additive optional wire field; stripped for non-owners.) */
  patrol?: { q: number; r: number }
  /** Player opt-out: this armed serf stands watch in place instead of patrolling.
   *  (Additive optional wire field; stripped for non-owners.) */
  noPatrol?: boolean
  /** COSMETIC ONLY: a hall crafter carrying material from the storage annex to
   *  the workbench holds this resource for the walk (drives the carry pose +
   *  cargo model). Never touches the stockpile — set at the annex, cleared when
   *  they reach the bench and start work. (Additive optional wire field.) */
  craftHaul?: Resource
}

/** Unit as sent over the wire (server path stripped; owners get a `wpath`
 *  snapshot of the remaining route so the client can draw it). */
export type UnitWire = Omit<Unit, 'path' | 'exploration'> & {
  /** Remaining route hexes (next hop first), owners only; capped length. */
  wpath?: { q: number; r: number }[]
}

// ---- Events ---------------------------------------------------------------------

/** 'decay': a building took weathering damage for lack of upkeep materials —
 *  clients float the damage over the building, no toast/chronicle. */
export type EventKind = 'info' | 'combat' | 'raid' | 'diplo' | 'build' | 'death' | 'decay'

export interface GameEvent {
  kind: EventKind
  msg: string
  q?: number
  r?: number
  /** If set, only this faction receives the event. */
  to?: string
}

// ---- World ------------------------------------------------------------------------

/**
 * A signed-in account, keyed in WorldState.users by `${provider}:${providerId}`.
 * One account owns exactly one player faction for the life of the world.
 */
export interface UserAccount {
  provider: string
  providerId: string
  /** The player faction this account controls. */
  factionId: string
  /** Every faction this account has ever controlled (current last). */
  factionHistory?: string[]
  /** Display name from the identity provider (seeds the faction name until named). */
  name: string
  /** Account email from the provider when available (Google); admin-only display. */
  email?: string
  /** True once the player has chosen their own kingdom name (stops the naming
   *  prompt on re-login and keeps the provider display name from overwriting it). */
  named?: boolean
  createdAt: number
  lastLogin: number
}

export interface WorldState {
  seed: number
  tick: number
  nextId: number
  createdAt: number
  chunks: Record<string, Chunk>
  factions: Record<string, Faction>
  buildings: Record<string, Building>
  units: Record<string, Unit>
}

export { Terrain }
